import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { Card, CardHeader, CardContent, CardFooter } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";
import { setToken } from "lib/api";
import { landingPath } from "lib/landing";
import LanguageSwitcher from "components/LanguageSwitcher";
import { EmailStep, MfaStep, PasswordStep } from "components/auth/LoginSteps";




function ChangePasswordStep({ tempToken, onSuccess }) {
  const { t } = useI18n();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  // Inline password strength checks
  const checks = [
    { label: t("login.pw.min8"), ok: newPassword.length >= 8 },
    { label: t("login.pw.upper"), ok: /[A-Z]/.test(newPassword) },
    { label: t("login.pw.lower"), ok: /[a-z]/.test(newPassword) },
    { label: t("login.pw.special"), ok: /[^a-zA-Z0-9]/.test(newPassword) },
    { label: t("login.pw.match"), ok: newPassword && newPassword === confirmPassword },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    if (newPassword !== confirmPassword) {
      setErrors([t("error.passwordsMismatch")]);
      return;
    }

    setLoading(true);
    try {
      const res = await api.post(
        "/api/auth/change-password/",
        { new_password: newPassword, confirm_password: confirmPassword },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      onSuccess(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setErrors(detail);
      } else if (typeof detail === "string") {
        setErrors([detail]);
      } else {
        setErrors([t("error.changePassword")]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          {t("login.change.title")}
        </h1>
        <p className="text-sm text-gray-500 mt-2">
          {t("login.change.subtitle")}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-gray-700">{t("login.newPassword")}</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNew ? "text" : "password"}
                placeholder={t("login.change.newPlaceholder")}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
                autoComplete="new-password"
                className="pr-10 bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-gray-700">{t("login.confirmPassword")}</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirm ? "text" : "password"}
                placeholder={t("login.change.confirmPlaceholder")}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="pr-10 bg-white text-gray-900 border-gray-300 placeholder:text-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Password strength indicators */}
          {newPassword && (
            <div className="space-y-1.5 pt-1">
              {checks.map((check, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {check.ok ? (
                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <X className="h-3.5 w-3.5 text-gray-300" />
                  )}
                  <span className={check.ok ? "text-emerald-500" : "text-gray-400"}>
                    {check.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {errors.length > 0 && (
            <div className="space-y-1 pt-1">
              {errors.map((err, i) => (
                <p key={i} className="text-xs text-red-500">{err}</p>
              ))}
            </div>
          )}

          <Button type="submit" className="w-full" size="lg" loading={loading}>
            {t("login.change.title")}
          </Button>
        </form>
      </CardContent>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { user, loginUser } = useAuth();
  const { t } = useI18n();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [mfaCode, setMfaCode] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // For change password flow
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [oidcEnabled, setOidcEnabled] = useState(false);
  const [faceLoading, setFaceLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(landingPath(user), { replace: true });
  }, [user, navigate]);

  // Return leg of a face login: the hosted verify page redirects back to
  // /liveness/login?status=passed&code=<one-time>. Exchange the code for a session.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const status = params.get("status");
    if (!code) return;
    // Strip the query so a refresh doesn't replay it.
    window.history.replaceState({}, "", "/liveness/login");
    if (status && status !== "passed") {
      setError(t("login.face.failed", "A verificação facial não foi aprovada."));
      return;
    }
    setFaceLoading(true);
    api.post("/api/auth/face/result/", { code })
      .then((res) => {
        if (res.data?.user && res.data?.token) {
          loginUser(res.data.user, res.data.token);
          navigate(landingPath(res.data.user));
        } else {
          setError(t("login.face.failed", "A verificação facial não foi aprovada."));
        }
      })
      .catch((err) => setError(err.response?.data?.detail
        || t("login.face.failed", "A verificação facial não foi aprovada.")))
      .finally(() => setFaceLoading(false));
  }, [loginUser, navigate, t]);

  const handleFaceLogin = async () => {
    setError("");
    setFaceLoading(true);
    try {
      const res = await api.post("/api/auth/face/start/", {});
      if (res.data?.verify_url) {
        window.location.href = res.data.verify_url;   // hosted liveness capture
        return;
      }
      setError(t("login.face.unavailable", "Login facial indisponível no momento."));
    } catch (err) {
      setError(err.response?.data?.detail
        || t("login.face.unavailable", "Login facial indisponível no momento."));
    } finally {
      setFaceLoading(false);
    }
  };

  useEffect(() => {
    api.get("/api/auth/config/")
      .then((res) => setOidcEnabled(!!res.data?.oidc_enabled))
      .catch(() => {});
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await api.post("/api/auth/email/", { email });
      if (res.data?.skip_mfa) {
        setLoading(false);
        setStep(3);
        return;
      }
    } catch (err) {
      // Always proceed to step 2 regardless of response
    } finally {
      setLoading(false);
    }
    if (step === 1) setStep(2);
  };

  const handleMfaSubmit = async (codeOrEvent) => {
    let codeString;
    if (typeof codeOrEvent === "string") {
      codeString = codeOrEvent;
    } else {
      codeOrEvent.preventDefault();
      codeString = mfaCode.join("");
    }

    if (codeString.length !== 6) {
      setError(t("error.mfaIncomplete"));
      return;
    }

    setError("");
    setLoading(true);

    try {
      await api.post("/api/auth/mfa/", { code: codeString });
      setStep(3);
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        t("login.mfaInvalid", "Código inválido. Tente novamente.");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await api.post("/api/auth/password/", { password });

      if (response.data?.must_change_password) {
        // User must change password — show change password form
        setTempToken(response.data.token);
        setMustChangePassword(true);
        setLoading(false);
        return;
      }

      if (response.data?.user && response.data?.token) {
        loginUser(response.data.user, response.data.token);
      }
      navigate(landingPath(response.data?.user));
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        t("error.wrongPassword");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChanged = (data) => {
    if (data?.user && data?.token) {
      loginUser(data.user, data.token);
    }
    navigate(landingPath(data?.user));
  };

  const goToStep = (targetStep) => {
    setError("");
    if (targetStep < step) {
      if (targetStep <= 2) setPassword("");
      if (targetStep <= 1) setMfaCode(["", "", "", "", "", ""]);
    }
    setStep(targetStep);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7] px-4 py-8">
      <div className="w-full max-w-[420px]">
        <div className="flex justify-end mb-3">
          <LanguageSwitcher />
        </div>
        <Card className="shadow-lg border-0 bg-white rounded-2xl">
          {mustChangePassword ? (
            <ChangePasswordStep
              tempToken={tempToken}
              onSuccess={handlePasswordChanged}
            />
          ) : (
            <>
              {step === 1 && (
                <EmailStep
                  email={email}
                  setEmail={setEmail}
                  onSubmit={handleEmailSubmit}
                  loading={loading}
                  error={error}
                  oidcEnabled={oidcEnabled}
                  onFaceLogin={handleFaceLogin}
                  faceLoading={faceLoading}
                />
              )}
              {step === 2 && (
                <MfaStep
                  code={mfaCode}
                  setCode={setMfaCode}
                  onSubmit={handleMfaSubmit}
                  onBack={() => goToStep(1)}
                  loading={loading}
                  error={error}
                />
              )}
              {step === 3 && (
                <PasswordStep
                  password={password}
                  setPassword={setPassword}
                  onSubmit={handlePasswordSubmit}
                  onBack={() => goToStep(2)}
                  loading={loading}
                  error={error}
                />
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
